import React, { useEffect, useMemo, useState } from "react";
import useTapLocationTask from "../../core/useTapLocationTask";
import { hitsZone } from "../../core/zones";
import CandleChart from "../../candle/CandleChart";
import { candleDestinations, candleZones, elementAnchor } from "../../candle/candleModel";
import NoWickDiagram from "./NoWickDiagram";
import Score from "./Score";
import Sentences from "../../core/Sentences";
import { COPY, PROMPTS, READ } from "./config";
import { explainCandleAnatomy } from "./explain";

const FOCUS =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1A1A1A]";

// One Candle Anatomy task on one candle: the six prompts, answered by tapping
// an unmarked candle. Guided (Beat 1), or unguided (Call It and every retry).
// After Submit it shows the all-correct reveal itself; when anything is wrong
// the Read swaps in the failure reveal (FailureReveal.jsx) instead.
//
//   mode          "guided" | "unguided"
//   hintEnabled   offer "Need a hint?" after inactivity (off for launch, READ.hint)
//   onHintOpened  the learner opened the hint (forfeits the bonus)
//   showNoWick    include the no-wick teaching visual in the reveal (Beat 3)
//   onSubmitted   (results) graded answer, as soon as Submit is pressed
//   onContinue    (results) the learner pressed the primary button to move on
//   continueLabel (results) => label for that button
export default function CandleTapTask({
  ohlc,
  mode,
  showNoWick,
  hintEnabled,
  onHintOpened,
  onSubmitted,
  onContinue,
  continueLabel,
}) {
  const zones = useMemo(() => candleZones(ohlc), [ohlc]);
  const prompts = useMemo(() => PROMPTS.map((p) => ({ ...p, zone: zones[p.id] })), [zones]);
  const guided = mode === "guided";
  // Teach holds on each step after a correct tap, so the explanation shown is
  // always the one for the step on screen; "Next" moves on.
  const task = useTapLocationTask(prompts, mode, { holdAfterCorrect: guided });
  const { active, activeIndex, answers, results, miss, held, guidedComplete } = task;

  const [announcement, setAnnouncement] = useState("");
  const [lastTeach, setLastTeach] = useState(null);
  const [visibleMiss, setVisibleMiss] = useState(null);
  const [hintAvailable, setHintAvailable] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);

  // The guided soft cue disappears on its own.
  useEffect(() => {
    if (!miss) return undefined;
    setVisibleMiss(miss);
    const t = setTimeout(() => setVisibleMiss(null), 800);
    return () => clearTimeout(t);
  }, [miss]);

  // "Need a hint?" appears after a period with no taps (only when enabled).
  useEffect(() => {
    if (!hintEnabled || results || hintAvailable) return undefined;
    const t = setTimeout(() => setHintAvailable(true), READ.hint.delayMs);
    return () => clearTimeout(t);
  }, [hintEnabled, results, hintAvailable, answers]);

  const openHint = () => {
    setHintOpen(true);
    if (onHintOpened) onHintOpened();
  };

  const handleTap = (pt) => {
    const promptLabel = active && active.label;
    const res = task.tap(pt);
    if (!res) return;
    if (guided) {
      const p = prompts.find((x) => x.id === res.promptId);
      if (res.correct) {
        setLastTeach(p.teach);
        setAnnouncement(`${promptLabel} placed. ${p.teach}`);
      } else {
        setAnnouncement("Try again.");
      }
    } else {
      setAnnouncement(`${promptLabel} recorded.`);
    }
  };

  const handleSubmit = () => {
    const graded = task.submit();
    if (!graded) return;
    setAnnouncement(graded.allCorrect ? "All six correct." : "Answers checked. See the explanation below.");
    if (onSubmitted) onSubmitted(graded);
  };

  const handleNext = () => {
    setLastTeach(null);
    task.next();
  };

  // --- what the chart shows -------------------------------------------------
  const markers = useMemo(() => {
    if (results) {
      const list = [];
      prompts.forEach((p) => {
        const r = results.byId[p.id];
        list.push({
          key: `${p.id}-you`,
          x: r.answer.x,
          y: r.answer.y,
          state: r.correct ? "correct" : "incorrect",
          label: p.label,
          // No "Correct" under the label: the "X of 6 correct" line says it.
          ...(r.correct ? { sub: null } : {}),
          side: "left",
        });
      });
      prompts.forEach((p) => {
        if (results.byId[p.id].correct) return;
        const a = elementAnchor(ohlc, p.id);
        list.push({ key: `${p.id}-fix`, x: a.x, y: a.y, state: "corrected", label: p.label, side: "right" });
      });
      return list;
    }
    return prompts
      .filter((p) => answers[p.id])
      .map((p) => {
        if (guided) {
          // A correct guided tap is shown where the element really is. Its
          // label goes on the right: the left column holds the callout.
          const a = elementAnchor(ohlc, p.id);
          return { key: p.id, x: a.x, y: a.y, state: "teach", label: p.label, side: "right" };
        }
        return { key: p.id, x: answers[p.id].x, y: answers[p.id].y, state: "neutral", label: p.label, side: "left" };
      });
  }, [answers, guided, ohlc, prompts, results]);

  const highlight = results
    ? { body: true, wicks: true }
    : guided
    ? { body: !!answers.body, wicks: !!answers.wick }
    : {};

  const showTarget = guided && active && !guidedComplete && !held;
  const zoneHints = showTarget ? active.zone : [];
  const target =
    showTarget
      ? { point: elementAnchor(ohlc, active.id), title: active.title, sub: active.sub }
      : null;

  const destinations = useMemo(() => {
    const list = candleDestinations(ohlc);
    if (!guided || !active) return list;
    // Guided only: the teaching target is announced, as it is shown visually.
    return list.map((d) => (hitsZone(d.point, active.zone) ? { ...d, label: `${d.label}, teaching target` } : d));
  }, [active, guided, ohlc]);

  const explanation = results ? explainCandleAnatomy(ohlc, zones, results) : null;
  const inProgress = !results && !guidedComplete;

  return (
    <div>
      <h1 className="font-montserrat font-bold text-title-subpage text-pageTitle">Candle Anatomy</h1>
      {/* Client: make the phase visible in the progress label —
          "TEACH · 1 OF 6", then "CALL IT · 1 OF 6" in the unguided section. */}
      {inProgress && active && (
        <p className="mt-0.5 text-xs font-semibold uppercase tracking-wider text-secondary">
          {guided ? "Teach" : "Call it"} · {activeIndex + 1} of {prompts.length}
        </p>
      )}

      {/* Guided: the instruction is drawn as a callout beside the candle, so
          it is repeated here for screen readers only. */}
      {inProgress && active && guided && (
        <p className="sr-only">
          {active.title}. {active.sub}
        </p>
      )}
      {inProgress && active && !guided && (
        <div className="mt-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
          <p className="font-semibold text-base text-bodyInk">{active.title}</p>
        </div>
      )}

      {/* Every reveal opens with the same "X of 6 correct" line (client: no
          screen keeps "Correct" in the labels without this header). */}
      {results && (
        <Score correct={prompts.filter((p) => results.byId[p.id].correct)} total={prompts.length} />
      )}

      <div className="mx-auto mt-1 md:mt-3 w-full max-w-[440px]">
        <CandleChart
          ohlc={ohlc}
          highlight={highlight}
          onTap={handleTap}
          disabled={!inProgress || !!held}
          zoneHints={zoneHints}
          target={target}
          markers={markers}
          miss={visibleMiss}
          destinations={inProgress ? destinations : []}
          destinationsLabel={active ? `Choose where ${active.label} is` : undefined}
        />
      </div>

      {inProgress && (
        <div className="mt-3 grid grid-cols-6 gap-1.5 md:gap-2" role="list" aria-label="Candle elements">
          {prompts.map((p, i) => {
            const isActive = active && active.id === p.id;
            const answered = !!answers[p.id];
            if (guided) {
              // As on the reference board: while the price points are being
              // placed, Body and Wick (a later group) are greyed; the other
              // price points stay at full strength.
              const isRegion = (id) => id === "body" || id === "wick";
              const future = i > activeIndex && isRegion(p.id) !== isRegion(active ? active.id : p.id);
              return (
                <div
                  key={p.id}
                  role="listitem"
                  aria-current={isActive ? "step" : undefined}
                  className={`min-h-[44px] flex items-center justify-center rounded-lg text-[13px] font-medium border ${
                    isActive
                      ? "border-2 border-primaryColor text-primaryColor"
                      : future
                      ? "border-gray-100 bg-gray-50 text-tertiary"
                      : "border-gray-200 text-bodyInk"
                  }`}
                >
                  {p.label}
                </div>
              );
            }
            return (
              <div key={p.id} role="listitem">
                <button
                  type="button"
                  onClick={() => task.selectPrompt(p.id)}
                  aria-pressed={isActive}
                  aria-label={`${p.label}${answered ? ", recorded" : ", not placed yet"}`}
                  className={`relative w-full min-h-[44px] rounded-lg text-[13px] font-medium bg-white ${FOCUS} ${
                    isActive ? "border-2 border-primaryColor text-primaryColor" : "border border-gray-200 text-bodyInk"
                  }`}
                >
                  {p.label}
                  {answered && (
                    <span aria-hidden="true" className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-primaryColor" />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {guided && (held || guidedComplete) && lastTeach && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-gray-200 px-4 py-3">
          <BulbIcon />
          <Sentences text={lastTeach} className="text-sm md:text-base text-primaryColor" />
        </div>
      )}

      {guided && held && !guidedComplete && (
        <button
          type="button"
          onClick={handleNext}
          className={`mt-4 w-full min-h-[44px] rounded-lg bg-primaryColor text-white font-semibold ${FOCUS}`}
        >
          Next
        </button>
      )}

      {guided && guidedComplete && onContinue && (
        <button
          type="button"
          onClick={() => onContinue(null)}
          className={`mt-4 w-full min-h-[44px] rounded-lg bg-primaryColor text-white font-semibold ${FOCUS}`}
        >
          Continue
        </button>
      )}

      {!guided && !results && (
        <div className="mt-4">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!task.canSubmit}
            className={`w-full min-h-[44px] rounded-lg font-semibold ${FOCUS} ${
              task.canSubmit ? "bg-primaryColor text-white" : "bg-gray-100 text-tertiary cursor-not-allowed"
            }`}
          >
            Submit
          </button>
          {!task.canSubmit && <p className="mt-2 text-center text-sm text-secondary">{COPY.submitGate}</p>}
          {hintEnabled && hintAvailable && !hintOpen && (
            <div className="mt-2 text-center">
              <button type="button" onClick={openHint} className={`text-sm font-semibold text-linkTeal ${FOCUS}`}>
                Need a hint?
              </button>
            </div>
          )}
          {hintOpen && (
            <div className="mt-3 flex items-start gap-3 rounded-xl border border-[#D7C588] bg-[#D7C588]/10 px-4 py-3">
              <BulbIcon />
              <p className="text-sm text-bodyInk">{COPY.hint}</p>
            </div>
          )}
        </div>
      )}

      {results && (
        <div className="mt-4">
          <div className="rounded-xl border border-gray-200 px-4 py-3">
            <p className="font-semibold text-bodyInk">Explanation</p>
            {explanation.map((line) => (
              <Sentences key={line} text={line} className="mt-1 text-sm text-bodyInk" />
            ))}
          </div>
          {showNoWick && <NoWickDiagram />}
          {onContinue && (
            <button
              type="button"
              onClick={() => onContinue(results)}
              className={`mt-4 w-full min-h-[44px] rounded-lg bg-primaryColor text-white font-semibold ${FOCUS}`}
            >
              {continueLabel ? continueLabel(results) : "Continue"}
            </button>
          )}
        </div>
      )}

      <div aria-live="polite" className="sr-only">
        {announcement}
      </div>
    </div>
  );
}

function BulbIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" className="shrink-0 mt-0.5">
      <path
        d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.6 10.8c.7.5 1.1 1.3 1.1 2.2h5c0-.9.4-1.7 1.1-2.2A6 6 0 0 0 12 3z"
        fill="#D7C588"
        fillOpacity="0.35"
        stroke="#1A1A1A"
        strokeOpacity="0.6"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
